import React, { useState, useContext, useEffect } from 'react';
import { Grid2, Typography, MenuItem } from '@mui/material';
import { withStyles } from '@mui/styles';
import { Controller, useFormContext } from 'react-hook-form';
import { COLORS } from '../../../utils/Application_Constants';
import ExpenseTextField from './ExpenseTextField';
import PaymentType from './PaymentType';
import PaymentAmount from './PaymentAmount';
import BillAmount from './BillAmount';
import PayAmount from './PayAmount';
import PayrollDate from './PayrollDate';
import ExpenseAutocomplete from './ExpenseAutocomplete';
import { states } from '../../Configurations/ExpenseEntryConfig';
import PaymentTypeMapping from './PaymentType.enum';
import { searchVendors } from '../../Services/ExpenseService';
import { ValidationModeContext } from '../../../contexts/VendorPaymentValidationModeContext';
import { ExpenseClassEnum } from './ExpenseClass.enum';
import { ExpenseStatusValuesEnum } from './ExpenseStatus.enum';

const styles = () => ({
    formHeader: {
        borderBottom: `1px solid ${COLORS.LT_MIDNIGHT25}`,
        paddingBottom: 8,
        marginBottom: 16,
    },
});

const PaymentDetailsForm = (props) => {
    const { classes, index, expense, isViewOnly } = props;
    const { control, watch, errors, setValue } = useFormContext();
    const [vendor, setVendor] = useState(null);
    const [vendorOptions, setVendorOptions] = useState([]);
    const [payToOrderOfOptions, setPayToOrderOfOptions] = useState([]);
    const [addressOptions, setAddressOptions] = useState([]);
    const validationMode = useContext(ValidationModeContext);

    useEffect(() => {
        if (expense.vendorId) {
            setValue(
                `expenseEntries[${index}].paymentVendor`,
                `${expense.vendorName}, ${expense.vendorCity}, ${expense.vendorState} ${expense.vendorZip}`
            );
            if (expense.vendorPayToOrderOf) {
                const newOption = {
                    payToOrderOf: expense.vendorPayToOrderOf,
                    city: expense.vendorCity,
                    state: expense.vendorState,
                    postalCode: expense.vendorZip,
                };
                setPayToOrderOfOptions([newOption]);
                setValue(`expenseEntries[${index}].vendorPayToOrderOf`, newOption);
            }
        }
    }, []);
    const currentPaymentType = watch(`expenseEntries[${index}].paymentType`);
    const currentExpenseCategory = watch(`expenseEntries[${index}].category`);
    const currentExpenseClass = watch(`expenseEntries[${index}].expenseClass`);

    const currentExpenseStatus = watch(`expenseEntries[${index}].status`);

    const validateField = (value) => {
        return (
            (validationMode === 'submit' ||
                currentExpenseStatus === ExpenseStatusValuesEnum.Approved) &&
            !value
        );
    };

    const shouldShowAmountConditionMsg =
        (Number(watch(`expenseEntries[${index}].billAmount`)) === 0 &&
            Number(watch(`expenseEntries[${index}].payAmount`)) === 0) ||
        (!watch(`expenseEntries[${index}].billAmount`) &&
            !watch(`expenseEntries[${index}].payAmount`));

    const shouldRenderSupplementalFields =
        currentExpenseCategory === 'Supplemental' &&
        currentExpenseClass === ExpenseClassEnum.Payment;

    const isMoneyOrder = currentPaymentType === PaymentTypeMapping.MoneyOrder.value;

    const getVendorOptions = async (searchText) => {
        const response = await searchVendors(searchText);
        const result = response
            .filter(
                (vendorEntry) =>
                    (vendorEntry.supplierStatus === 'Active' ||
                        vendorEntry.supplierStatus === 'Hold') &&
                    vendorEntry.defaultPaymentType === currentPaymentType
            )
            .map((vendorEntry) => ({
                ...vendorEntry,
                vendorInfo: `${vendorEntry.supplierName}, ${vendorEntry.city}, ${vendorEntry.state} ${vendorEntry.postalCode}`,
            }))
            .sort((a, b) => a.vendorInfo.localeCompare(b.vendorInfo));
        setVendorOptions(result);
        return result;
    };

    const getPayToOrderOfOptions = (selectedVendor) => {
        if (selectedVendor == null) {
            return [];
        }
        const options = vendorOptions.filter(
            (option) =>
                option.supplierName === selectedVendor.supplierName &&
                option.payToOrderOf !== null &&
                option.payToOrderOf.trim() !== ''
        );
        setPayToOrderOfOptions(options);

        if (options.length === 0) {
            setAddressOptions([selectedVendor]);
        }
    };

    const getAddressOptions = (selectedPayToOrderOf) => {
        if (selectedPayToOrderOf == null) {
            setAddressOptions([]);
            return [];
        }
        const options = vendorOptions.filter(
            (option) => option.payToOrderOf === selectedPayToOrderOf
        );
        setAddressOptions(options);
    };

    const resetPayToOrderOfOptions = () => {
        setPayToOrderOfOptions([]);
        setValue(`expenseEntries[${index}].vendorPayToOrderOf`, '');
    };

    const resetPaymentForm = () => {
        setValue(`expenseEntries[${index}].paymentVendor`, '');
        setVendor({});
        setVendorOptions(['']);
        resetPayToOrderOfOptions();
        updatePaymentFields({ city: '', state: '', postalCode: '' }, false);
    };

    const selectVendor = (selectedVendor) => {
        resetPayToOrderOfOptions();
        setAddressOptions([]);
        setVendor(selectedVendor);
        getPayToOrderOfOptions(selectedVendor);
        if (payToOrderOfOptions.length < 2) {
            updatePaymentFields(selectedVendor);
        }
    };

    const selectPayToOrderOf = (selection) => {
        getAddressOptions(selection.payToOrderOf);
    };

    const updatePaymentFields = (value, validate = true) => {
        if (value) {
            setValue(`expenseEntries[${index}].vendorCity`, value.city, {
                shouldValidate: validate,
            });
            setValue(`expenseEntries[${index}].vendorState`, value.state, {
                shouldValidate: validate,
            });
            setValue(`expenseEntries[${index}].vendorZip`, value.postalCode, {
                shouldValidate: validate,
            });
        }
    };

    const updatePaymentTotalAmount = () => {
        setValue(
            `expenseEntries[${index}].amount`,
            Number(watch(`expenseEntries[${index}].payAmount`)) +
                Number(watch(`expenseEntries[${index}].billAmount`))
        );
    };

    return (
        <>
            <Typography variant="h5" component="h5" className={classes.formHeader}>
                Payment Details
            </Typography>
            {shouldRenderSupplementalFields ? (
                <Grid2 container spacing={3}>
                    <BillAmount
                        index={index}
                        expense={expense}
                        isViewOnly={isViewOnly}
                        shouldShowAmountConditionMsg={shouldShowAmountConditionMsg}
                        updatePaymentTotalAmount={updatePaymentTotalAmount}
                    />
                    <PayAmount
                        index={index}
                        expense={expense}
                        isViewOnly={isViewOnly}
                        shouldShowAmountConditionMsg={shouldShowAmountConditionMsg}
                        updatePaymentTotalAmount={updatePaymentTotalAmount}
                    />
                    <PayrollDate
                        expense={expense}
                        index={index}
                        isViewOnly={isViewOnly}
                        isRequired={expense?.status == ExpenseStatusValuesEnum.Approved}
                    />
                </Grid2>
            ) : (
                <Grid2 container spacing={3}>
                    <PaymentType
                        index={index}
                        expense={expense}
                        isViewOnly={isViewOnly}
                        newSelection={(e) => {
                            resetPaymentForm();
                        }}
                    />
                    <Grid2 item size={8}>
                        <Controller
                            control={control}
                            name={`expenseEntries[${index}].paymentVendor`}
                            rules={{
                                validate: (value) => {
                                    if (validateField(value)) {
                                        return 'Vendor is required';
                                    }
                                    return true; // Passes in save mode
                                },
                            }}
                            render={({ onChange, value }) => (
                                <ExpenseAutocomplete
                                    id="payment-vendor-search"
                                    onChange={(e) => {
                                        selectVendor(e);
                                        onChange(e);
                                    }}
                                    value={value}
                                    inputProps={{ disableUnderline: true }}
                                    minLength={3}
                                    label="Vendor"
                                    required
                                    error={Boolean(errors.expenseEntries?.[index]?.paymentVendor)}
                                    helperText={
                                        errors.expenseEntries?.[index]?.paymentVendor?.message
                                    }
                                    getOptionsMethod={getVendorOptions}
                                    placeholder="Search by name, city, or zip"
                                    optionLabelKey="vendorInfo"
                                    optionSelectedKey="supplierId"
                                    showRefreshVendorButton
                                    isViewOnly={isViewOnly}
                                />
                            )}
                        />
                    </Grid2>
                    {(payToOrderOfOptions.length > 0 || expense.vendorPayToOrderOf) && (
                        <Grid2 item size={8}>
                            <Controller
                                control={control}
                                name={`expenseEntries[${index}].vendorPayToOrderOf`}
                                rules={{
                                    validate: (value) => {
                                        if (validateField(value)) {
                                            return 'Pay to the order of is required';
                                        }
                                        return true; // Passes in save mode
                                    },
                                }}
                                render={({ onChange, value }) => (
                                    <ExpenseTextField
                                        id="pay-to-order-of-search"
                                        select
                                        onChange={(e) => {
                                            selectPayToOrderOf(e.target.value);
                                            onChange(e);
                                        }}
                                        value={value}
                                        label="Pay to the order of"
                                        placeholder="Select"
                                        required
                                        error={Boolean(
                                            errors.expenseEntries?.[index]?.vendorPayToOrderOf
                                        )}
                                        helperText={
                                            errors.expenseEntries?.[index]?.vendorPayToOrderOf
                                                ?.message
                                        }
                                        disabled={
                                            vendor == null || payToOrderOfOptions.length === 0
                                        }
                                    >
                                        <MenuItem value="" disabled>
                                            Select
                                        </MenuItem>
                                        {payToOrderOfOptions.map((option, i) => (
                                            <MenuItem
                                                key={option.id}
                                                value={option}
                                                id={`provider-search-option-${i}`}
                                            >
                                                {`${option.payToOrderOf}, ${option.city}, ${option.state} ${option.postalCode}`}
                                            </MenuItem>
                                        ))}
                                    </ExpenseTextField>
                                )}
                            />
                        </Grid2>
                    )}
                    <PaymentAmount index={index} expense={expense} isViewOnly={isViewOnly} />
                    {isMoneyOrder && (
                        <Grid2 item size={12}>
                            <Controller
                                control={control}
                                name={`expenseEntries[${index}].paymentAddress`}
                                rules={{
                                    validate: (value) => {
                                        if (validateField(value)) {
                                            return 'Address is required';
                                        }
                                        return true; // Passes in save mode
                                    },
                                }}
                                render={({ onChange, value }) => (
                                    <ExpenseTextField
                                        id="address-name"
                                        select
                                        onChange={(e) => {
                                            const newValue = e.target.value;
                                            onChange(newValue);
                                        }}
                                        value={!value ? '' : value}
                                        label="Address"
                                        required
                                        disabled={vendor == null || addressOptions.length < 1}
                                        error={Boolean(
                                            errors.expenseEntries?.[index]?.paymentAddress
                                        )}
                                        helperText={
                                            errors.expenseEntries?.[index]?.paymentAddress?.message
                                        }
                                    >
                                        <MenuItem value="" disabled>
                                            Select
                                        </MenuItem>
                                        {addressOptions.map((option, i) => (
                                            <MenuItem
                                                key={option.id}
                                                value={option}
                                                id={`address-option-${i}`}
                                            >
                                                {option.addressLine1}
                                            </MenuItem>
                                        ))}
                                    </ExpenseTextField>
                                )}
                            />
                        </Grid2>
                    )}
                    <Grid2 item size={4}>
                        <Controller
                            control={control}
                            name={`expenseEntries[${index}].vendorCity`}
                            rules={{
                                validate: (value) => {
                                    if (validateField(value)) {
                                        return 'City is required';
                                    }
                                    return true; // Passes in save mode
                                },
                            }}
                            render={({ onChange, value }) => (
                                <ExpenseTextField
                                    required
                                    disabled
                                    id="vendor-city"
                                    onChange={onChange}
                                    inputProps={{ maxLength: 50 }}
                                    value={value}
                                    placeholder="City"
                                    label="City"
                                    error={Boolean(errors.expenseEntries?.[index]?.vendorCity)}
                                    helperText={errors.expenseEntries?.[index]?.vendorCity?.message}
                                />
                            )}
                        />
                    </Grid2>
                    <Grid2 item size={4}>
                        <Controller
                            control={control}
                            name={`expenseEntries[${index}].vendorState`}
                            defaultValue={expense.state}
                            rules={{
                                validate: (value) => {
                                    if (validateField(value)) {
                                        return 'State is required';
                                    }
                                    return true; // Passes in save mode
                                },
                            }}
                            render={({ onChange, value }) => (
                                <ExpenseTextField
                                    id="payment-state-abbreviation"
                                    placeholder="State"
                                    onChange={onChange}
                                    select
                                    required
                                    error={Boolean(errors.expenseEntries?.[index]?.paymentState)}
                                    helperText={
                                        errors.expenseEntries?.[index]?.paymentState?.message
                                    }
                                    value={value}
                                    label="State"
                                    disabled
                                >
                                    <MenuItem disabled value="">
                                        Please select a state
                                    </MenuItem>
                                    {states.map((state, i) => (
                                        <MenuItem
                                            key={state.value}
                                            value={state.value}
                                            id={`vendor-state-abbreviation-menu-option-${i}`}
                                        >
                                            {state.name}
                                        </MenuItem>
                                    ))}
                                </ExpenseTextField>
                            )}
                        />
                    </Grid2>
                    <Grid2 item size={4}>
                        <Controller
                            control={control}
                            name={`expenseEntries[${index}].vendorZip`}
                            rules={{
                                validate: (value) => {
                                    if (validateField(value)) {
                                        return 'Zip is required';
                                    }
                                    return true; // Passes in save mode
                                },
                            }}
                            render={({ onChange, value }) => (
                                <ExpenseTextField
                                    required
                                    disabled
                                    id="vendorZip"
                                    onChange={onChange}
                                    inputProps={{ maxLength: 50 }}
                                    value={value}
                                    placeholder="Zip"
                                    label="Zip"
                                    error={Boolean(errors.expenseEntries?.[index]?.vendorZip)}
                                    helperText={errors.expenseEntries?.[index]?.vendorZip?.message}
                                />
                            )}
                        />
                    </Grid2>
                </Grid2>
            )}
        </>
    );
};

export default withStyles(styles)(PaymentDetailsForm);
